/**
 * SettingsBillingPage — Billing settings placeholder
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { useChatContextStore } from '@/stores/chat-context-store';

interface CreditState {
  state: string;
  monthlyFreeCredits: number;
  estimatedConsumedCredits: number;
  remainingCredits: number;
  currentMonthKey?: string;
  openingBalance?: number;
  starterGrantApplied?: boolean;
  currentMonthSettlementApplied?: boolean;
  openOnboardingRecords: number;
  openOnboardingTasks: number;
  onboardingRecords: number;
  onboardingTasks: number;
  upgradeRecommended: boolean;
  source: string;
  note: string;
  allowedInStarter: string[];
  gatedForPaid: string[];
}

interface CreditLedgerEntry {
  id: string;
  entry_type: string;
  delta_credits: number;
  source_type: string | null;
  source_ref: string | null;
  notes: string | null;
  created_at: string | null;
}

interface CreditLedgerResponse {
  monthlyFreeCredits: number;
  ledgerDelta: number;
  balance: number;
  currentMonthKey?: string;
  currentMonthSettlementApplied?: boolean;
  openingBalance?: number;
  rolloverCap?: number;
  starterGrantApplied?: boolean;
  rolloverApplied?: boolean;
  items: CreditLedgerEntry[];
  note: string;
}

interface UsageEventEntry {
  id: string;
  action_key: string;
  units: number;
  credits_consumed: number;
  source_type: string | null;
  source_ref: string | null;
  notes: string | null;
  created_at: string | null;
}

interface UsageEventsResponse {
  pricing: Record<string, number>;
  items: UsageEventEntry[];
  summaryByAction: Record<string, { units: number; creditsConsumed: number }>;
}

interface BillingAutomationStatus {
  currentMonthKey: string;
  previousMonthKey: string;
  maintenanceMode: string;
  currentMonth: {
    settlementApplied: boolean;
    grantApplied: boolean;
  };
  previousMonth: {
    monthKey: string;
    settlementApplied: boolean;
  };
  usageEventsTotal: number;
  currentMonthUsageEvents: number;
  meteredActions: string[];
  recommendedNextSteps: string[];
}

export default function SettingsBillingPage() {
  const { t } = useTranslation();
  const mergePageContext = useChatContextStore((state) => state.mergePageContext);
  const [creditState, setCreditState] = useState<CreditState | null>(null);
  const [creditLedger, setCreditLedger] = useState<CreditLedgerResponse | null>(null);
  const [usageEvents, setUsageEvents] = useState<UsageEventsResponse | null>(null);
  const [automationStatus, setAutomationStatus] = useState<BillingAutomationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState<null | 'grant' | 'topup' | 'rollover' | 'settlement' | 'maintenance'>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadCreditState();
  }, []);

  useEffect(() => {
    const missingItems: string[] = [];
    if (!automationStatus?.currentMonth.settlementApplied) missingItems.push('Current month settlement is still missing');
    if (!automationStatus?.currentMonth.grantApplied) missingItems.push('Current month starter grant is still missing');

    mergePageContext({
      taskContext: {
        taskType: 'billing_credit_governance',
        taskGoal: 'Keep starter credits, usage metering, and monthly maintenance healthy for tenant operations.',
        currentStep: loading ? 'loading' : 'review_credit_state',
        completionCriteria: [
          'Monthly settlement is applied',
          'Starter grant baseline exists',
          'Usage summary and upgrade risk are reviewed',
        ],
        missingItems,
        blockingRisks: [
          ...(creditState?.upgradeRecommended ? ['Upgrade review is recommended due to credit pressure'] : []),
          ...(creditState && creditState.remainingCredits <= 0 ? ['Remaining credits are depleted'] : []),
        ],
        recommendedNextActions: [
          automationStatus?.currentMonth.settlementApplied ? 'Review usage mix and upgrade timing' : 'Run monthly settlement',
          creditState?.upgradeRecommended ? 'Open commercial upgrade follow-up' : 'Keep starter mode under observation',
          'Review metered actions before enabling broader automation',
        ],
      },
      executionContext: {
        workspace: 'billing_settings',
        currentMonthKey: creditLedger?.currentMonthKey ?? creditState?.currentMonthKey ?? null,
        balance: creditLedger?.balance ?? creditState?.remainingCredits ?? null,
        monthlyFreeCredits: creditState?.monthlyFreeCredits ?? null,
        usageEventsTotal: automationStatus?.usageEventsTotal ?? usageEvents?.items.length ?? 0,
        upgradeRecommended: creditState?.upgradeRecommended ?? false,
      },
      permissionContext: {
        canRunMaintenance: true,
        canGrantStarter: true,
        canTopUp: true,
        canRunRollover: true,
      },
      contextBudget: {
        detailLevel: 'medium',
        maxKeyFacts: 8,
        preferActionableSummary: true,
      },
    });

    return () => {
      mergePageContext({
        taskContext: undefined,
        executionContext: undefined,
        permissionContext: undefined,
        contextBudget: undefined,
      });
    };
  }, [
    automationStatus,
    creditLedger?.balance,
    creditLedger?.currentMonthKey,
    creditState,
    loading,
    mergePageContext,
    usageEvents?.items.length,
  ]);

  async function loadCreditState() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<CreditState>('/billing/credit-state');
      setCreditState(data);
      const ledger = await api.get<CreditLedgerResponse>('/billing/credit-ledger');
      setCreditLedger(ledger);
      const usage = await api.get<UsageEventsResponse>('/billing/usage-events');
      setUsageEvents(usage);
      const automation = await api.get<BillingAutomationStatus>('/billing/automation/status');
      setAutomationStatus(automation);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit state');
    } finally {
      setLoading(false);
    }
  }

  async function runLedgerAction(kind: 'grant' | 'topup' | 'rollover' | 'settlement' | 'maintenance') {
    setActionBusy(kind);
    setError(null);
    try {
      if (kind === 'grant') {
        await api.post('/billing/credit-ledger/grant-starter', {});
      } else if (kind === 'topup') {
        await api.post('/billing/credit-ledger/top-up', { credits: 120, notes: 'Demo/manual top-up from billing surface' });
      } else if (kind === 'settlement') {
        await api.post('/billing/credit-ledger/run-monthly-settlement', {});
      } else if (kind === 'maintenance') {
        await api.post('/billing/automation/run-maintenance', { includePreviousMonth: true });
      } else {
        await api.post('/billing/credit-ledger/rollover', {});
      }
      await loadCreditState();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update credit ledger');
    } finally {
      setActionBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6 max-w-2xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          {t('settings.billing', 'Billing')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t('settings.billingDesc', 'Manage your subscription and billing details.')}
        </p>
        </div>
        <button
          type="button"
          onClick={() => void loadCreditState()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {creditState ? (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border bg-card p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Monthly Free Credits</div>
            <div className="mt-2 text-2xl font-semibold">{creditState.monthlyFreeCredits}</div>
            <div className="mt-1 text-xs text-muted-foreground">state: {creditState.state}</div>
          </div>
          <div className="rounded-xl border bg-card p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Consumed</div>
            <div className="mt-2 text-2xl font-semibold">{creditState.estimatedConsumedCredits}</div>
            <div className="mt-1 text-xs text-muted-foreground">source: {creditState.source}</div>
          </div>
          <div className="rounded-xl border bg-card p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Remaining</div>
            <div className="mt-2 text-2xl font-semibold">{creditState.remainingCredits}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {creditState.upgradeRecommended ? 'upgrade review recommended' : 'starter mode still healthy'}
            </div>
          </div>
        </div>
      ) : null}

      {creditLedger ? (
        <div className="rounded-xl border bg-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium">Credit Ledger</div>
              <p className="text-sm text-muted-foreground mt-2">{creditLedger.note}</p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {creditLedger.currentMonthKey ? <span className="rounded border px-2 py-1">month {creditLedger.currentMonthKey}</span> : null}
                <span className={`rounded border px-2 py-1 ${creditLedger.starterGrantApplied ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                  starter grant {creditLedger.starterGrantApplied ? 'applied' : 'pending'}
                </span>
                <span className={`rounded border px-2 py-1 ${creditLedger.currentMonthSettlementApplied ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-muted bg-muted/40 text-muted-foreground'}`}>
                  settlement {creditLedger.currentMonthSettlementApplied ? 'applied' : 'pending'}
                </span>
                <span className={`rounded border px-2 py-1 ${creditLedger.rolloverApplied ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-muted bg-muted/40 text-muted-foreground'}`}>
                  rollover {creditLedger.rolloverApplied ? 'applied' : 'not applied'}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void runLedgerAction('maintenance')}
                disabled={loading || actionBusy !== null}
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
              >
                Run Automation
              </button>
              <button
                type="button"
                onClick={() => void runLedgerAction('settlement')}
                disabled={loading || actionBusy !== null}
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
              >
                Run Monthly Settlement
              </button>
              <button
                type="button"
                onClick={() => void runLedgerAction('grant')}
                disabled={loading || actionBusy !== null}
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
              >
                Grant Starter
              </button>
              <button
                type="button"
                onClick={() => void runLedgerAction('topup')}
                disabled={loading || actionBusy !== null}
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
              >
                Top-up 120
              </button>
              <button
                type="button"
                onClick={() => void runLedgerAction('rollover')}
                disabled={loading || actionBusy !== null}
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
              >
                Run Rollover
              </button>
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Baseline</div>
              <div className="mt-1 font-semibold">{creditLedger.monthlyFreeCredits}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Opening Balance</div>
              <div className="mt-1 font-semibold">{creditLedger.openingBalance ?? 0}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Ledger Delta</div>
              <div className="mt-1 font-semibold">{creditLedger.ledgerDelta}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Balance</div>
              <div className="mt-1 font-semibold">{creditLedger.balance}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Rollover Cap</div>
              <div className="mt-1 font-semibold">{creditLedger.rolloverCap ?? '—'}</div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {creditLedger.items.length > 0 ? creditLedger.items.slice(0, 8).map((entry) => (
              <div key={entry.id} className="flex items-start justify-between gap-3 rounded-lg border p-3 text-sm">
                <div>
                  <div className="font-medium">{entry.entry_type}</div>
                  <div className="text-xs text-muted-foreground">{entry.notes ?? entry.source_type ?? 'credit event'}</div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${entry.delta_credits >= 0 ? 'text-green-700' : 'text-amber-700'}`}>
                    {entry.delta_credits >= 0 ? '+' : ''}{entry.delta_credits}
                  </div>
                  <div className="text-xs text-muted-foreground">{entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}</div>
                </div>
              </div>
            )) : (
              <div className="text-sm text-muted-foreground">No credit ledger entries yet.</div>
            )}
          </div>
        </div>
      ) : null}

      {creditState ? (
        <div className="rounded-xl border bg-card p-5">
          <div className="text-sm font-medium">Activity-based Credit Snapshot</div>
          <p className="text-sm text-muted-foreground mt-2">{creditState.note}</p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Onboarding Activity</div>
              <div className="mt-2 text-sm">
                records {creditState.onboardingRecords} · open records {creditState.openOnboardingRecords}
              </div>
              <div className="mt-1 text-sm">
                tasks {creditState.onboardingTasks} · open tasks {creditState.openOnboardingTasks}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Starter vs Paid</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {creditState.allowedInStarter.map((item) => (
                  <span key={item} className="rounded border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">{item}</span>
                ))}
                {creditState.gatedForPaid.map((item) => (
                  <span key={item} className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">{item}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {usageEvents ? (
        <div className="rounded-xl border bg-card p-5">
          <div className="text-sm font-medium">Usage Metering</div>
          <p className="text-sm text-muted-foreground mt-2">
            关键 onboarding / AI action 现在通过 usage event 记量，再同步写入 credit ledger。
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Action Pricing</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(usageEvents.pricing).map(([actionKey, unitPrice]) => (
                  <span key={actionKey} className="rounded border px-2 py-1 text-xs">
                    {actionKey} · {unitPrice}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Usage Summary</div>
              <div className="mt-2 space-y-2">
                {Object.entries(usageEvents.summaryByAction).length > 0 ? Object.entries(usageEvents.summaryByAction).map(([actionKey, summary]) => (
                  <div key={actionKey} className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                    <span>{actionKey}</span>
                    <span className="text-muted-foreground">{summary.units} units · {summary.creditsConsumed} credits</span>
                  </div>
                )) : (
                  <div className="text-sm text-muted-foreground">No usage events yet.</div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {usageEvents.items.slice(0, 8).map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-3 rounded-lg border p-3 text-sm">
                <div>
                  <div className="font-medium">{item.action_key}</div>
                  <div className="text-xs text-muted-foreground">{item.notes ?? item.source_type ?? 'usage event'}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-amber-700">-{item.credits_consumed}</div>
                  <div className="text-xs text-muted-foreground">{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {automationStatus ? (
        <div className="rounded-xl border bg-card p-5">
          <div className="text-sm font-medium">Billing Automation</div>
          <p className="text-sm text-muted-foreground mt-2">
            这是给 scheduler 或人工运营使用的 maintenance 入口。当前月打开 billing 页面会自动 ensure 基线，这里负责把月度维护动作显性化。
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Maintenance Status</div>
              <div className="mt-2 space-y-2 text-sm">
                <div>mode: {automationStatus.maintenanceMode}</div>
                <div>current month: {automationStatus.currentMonthKey}</div>
                <div>previous month: {automationStatus.previousMonthKey}</div>
                <div>current settlement: {automationStatus.currentMonth.settlementApplied ? 'ready' : 'missing'}</div>
                <div>current starter grant: {automationStatus.currentMonth.grantApplied ? 'ready' : 'missing'}</div>
                <div>previous settlement: {automationStatus.previousMonth.settlementApplied ? 'ready' : 'missing'}</div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Coverage</div>
              <div className="mt-2 space-y-2 text-sm">
                <div>total usage events: {automationStatus.usageEventsTotal}</div>
                <div>current month usage: {automationStatus.currentMonthUsageEvents}</div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {automationStatus.meteredActions.map((action) => (
                    <span key={action} className="rounded border px-2 py-1 text-xs">{action}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {automationStatus.recommendedNextSteps.map((step) => (
              <div key={step} className="rounded border px-3 py-2 text-sm">
                {step}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border bg-card p-6">
          <div className="text-sm font-medium">Starter Free Mode</div>
          <p className="text-sm text-muted-foreground mt-2">
            新 tenant/member 默认进入 starter free mode，提供 monthly free credits，优先用于 guided demo、trial 和轻量 AI action。
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">guided demo</span>
            <span className="rounded border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">starter package preview</span>
            <span className="rounded border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">basic AI guidance</span>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <div className="text-sm font-medium">Upgrade / Paid Mode</div>
          <p className="text-sm text-muted-foreground mt-2">
            credits 不足时不应 hard-stop 基础访问，而应保留 workspace visibility，并限制 premium AI compose、deep simulation 和 advanced runtime automation。
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">premium AI compose</span>
            <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">deep simulation</span>
            <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">advanced automation</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-8 text-center text-muted-foreground">
        <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm">{t('settings.billingComingSoon', 'Paid plan packaging and full usage automation are the remaining billing implementation steps.')}</p>
      </div>
    </div>
  );
}
